import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { AlertCircle, ChevronDown, ChevronRight, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatPercent } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PayablesPanel({ embedded = false }) {
  const { getAuthHeaders } = useAuth();
  const [payables, setPayables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const fetchPayables = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/syndications/capital-payables`, {
        headers: { ...getAuthHeaders(), 'X-Tenant': 'live' },
      });
      setPayables(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load payables');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchPayables(); }, [fetchPayables]);

  const toggleExpand = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalOwed = payables.reduce((sum, p) => sum + p.total_net_owed, 0);

  return (
      <div className="space-y-6" data-testid="capital-payables-page">
        <div>
          {embedded
            ? <h2 className="text-lg font-semibold text-slate-900">Payables — partners still owed capital</h2>
            : <h1 className="text-2xl font-bold text-slate-900">Syndication Payables</h1>}
          <p className="text-sm text-slate-500 mt-1">Settlement view for Partners with outstanding balances</p>
        </div>

        {/* Summary */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Total Net Owed to Partners</CardTitle>
            <DollarSign className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-amber-600" data-testid="payables-total-owed">
              {formatCurrency(totalOwed)}
            </p>
            <p className="text-sm text-slate-500 mt-1">{payables.length} partner(s) with outstanding balances</p>
          </CardContent>
        </Card>

        {/* Payables List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : payables.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No outstanding payables</p>
              <p className="text-sm text-slate-400 mt-1">All partner balances are settled or no partner syndications exist.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {payables.map((partner) => {
              const isExpanded = expandedRows.has(partner.syndicator_id);
              return (
                <Card key={partner.syndicator_id} data-testid={`payable-card-${partner.syndicator_id}`}>
                  <button
                    onClick={() => toggleExpand(partner.syndicator_id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors rounded-t-lg"
                    data-testid={`payable-toggle-${partner.syndicator_id}`}
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                      <div className="text-left">
                        <p className="font-semibold text-slate-900">{partner.syndicator_name}</p>
                        <p className="text-xs text-slate-500">{partner.deals.length} deal(s)</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-amber-600">{formatCurrency(partner.total_net_owed)}</p>
                      <Badge variant="outline" className="text-xs">Partner</Badge>
                    </div>
                  </button>

                  {isExpanded && (
                    <CardContent className="pt-0 pb-4">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Deal</TableHead>
                              <TableHead className="text-right">Ownership</TableHead>
                              <TableHead className="text-right">Funded</TableHead>
                              <TableHead className="text-right">Principal Returned</TableHead>
                              <TableHead className="text-right">Fee Credit</TableHead>
                              <TableHead className="text-right">Adjusted Capital</TableHead>
                              <TableHead className="text-right">Net Owed</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {partner.deals.map((deal, i) => (
                              <TableRow key={deal.deal_id} data-testid={`payable-deal-${deal.deal_id}`}>
                                <TableCell className="font-medium">{deal.deal_name}</TableCell>
                                <TableCell className="text-right">{formatPercent(deal.ownership_percentage)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(deal.funded_amount)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(deal.principal_returned)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(deal.partner_fee_credit)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(deal.adjusted_capital)}</TableCell>
                                <TableCell className="text-right font-semibold text-amber-600">{formatCurrency(deal.net_owed)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
  );
}

// 11/03 (Josh): "Payables … could be on the same screen as distributions" — the panel now lives at the bottom of
// Distributions (SyndicationDashboard.js); this page stays for the old URL.
export default function SyndicationPayables() {
  return <Layout><PayablesPanel /></Layout>;
}
